/**
 * Authentication hook
 * Manages user authentication state via httpOnly JWT cookie
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { getMe, logout as logoutApi } from '@/lib/api/auth';
import type { User } from '@/types/api';
import { authKeys } from '@/lib/query-keys';

// Re-export for backwards compatibility
export { authKeys };

/**
 * Hook return type
 */
export interface UseAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  error: Error | null;
  logout: () => void;
}

/**
 * Hook for logout functionality only
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return async () => {
    await logoutApi();
    queryClient.clear();
    navigate({ to: '/login' });
  };
}

/**
 * Custom hook for managing authentication state
 * Calls GET /auth/me to verify the JWT cookie
 */
export function useAuth(): UseAuthReturn {
  const {
    data: verifyData,
    isLoading,
    error,
  } = useQuery({
    queryKey: authKeys.user(),
    queryFn: () => getMe(),
    retry: false,
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 120, // 2 hours
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const logout = useLogout();

  const isAuthenticated = !!(verifyData?.valid && verifyData.user);
  const user = verifyData?.user || null;

  return {
    isAuthenticated,
    isLoading,
    user,
    error: error as Error | null,
    logout,
  };
}

/**
 * Hook to get current user (requires authentication)
 */
export function useCurrentUser(): User | null {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  return user;
}

/**
 * Hook for login functionality
 */
export function useLogin() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (credentials: { name: string }) => {
      const { login } = await import('@/lib/api/auth');
      return login(credentials);
    },
    onSuccess: (data) => {
      // Cookie is set by the backend response — just update the cache
      queryClient.setQueryData(authKeys.user(), {
        valid: true,
        user: data.user,
      });

      navigate({ to: '/' });
    },
  });
}
